// ── Adapter Supabase do IPanelMetricsRepository ───────────────────
// Contagens/leituras dos gráficos do Meu Painel. Única camada que conhece as
// tabelas leads/meetings/cards/commissions/sales/activities.

import { getSupabase } from '@infra/supabase/client';
import type {
  IPanelMetricsRepository,
  LeadCountFilter,
  MeetingCountFilter,
  UserMeetingCountFilter,
  NegotiationLeadsFilter,
  ActivityCountFilter,
  LostCardCountFilter,
  CardCountFilter,
  CommissionCountFilter,
  StaleLeadRow,
  NegotiationLeadRow,
  SaleByCardRow,
} from '@modules/panelMetrics';

type MeetingStatus = 'done' | 'cancelled' | 'scheduled' | 'confirmed' | 'no_show';

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

export class SupabasePanelMetricsRepository implements IPanelMetricsRepository {
  countLeads = async ({ workspaceId, sdrId, closerId, ativo, status, statusNot, startISO, endISO }: LeadCountFilter): Promise<number> => {
    let q = client().from('leads').select('id', { count: 'exact', head: true }).eq('workspace_id', workspaceId);
    if (ativo !== undefined) q = q.eq('ativo', ativo);
    if (sdrId) q = q.eq('sdr_id', sdrId);
    if (closerId) q = q.eq('closer_id', closerId);
    if (status) q = q.eq('status', status as 'new' | 'contacted' | 'qualified' | 'proposal' | 'won' | 'lost');
    if (statusNot) q = q.neq('status', statusNot as 'new' | 'contacted' | 'qualified' | 'proposal' | 'won' | 'lost');
    if (startISO) q = q.gte('created_at', startISO);
    if (endISO) q = q.lt('created_at', endISO);
    const { count } = await q;
    return count || 0;
  };

  countUserMeetings = async ({ workspaceId, userId, status, excludeCancelled, startISO, endISO }: UserMeetingCountFilter): Promise<number> => {
    let q = client().from('meetings').select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .or(`closer_id.eq.${userId},sdr_id.eq.${userId}`);
    if (status) q = q.eq('status', status as MeetingStatus);
    if (excludeCancelled) q = q.neq('status', 'cancelled');
    if (startISO) q = q.gte('scheduled_at', startISO);
    if (endISO) q = q.lt('scheduled_at', endISO);
    const { count } = await q;
    return count || 0;
  };

  countActivities = async ({ workspaceId, assignedTo, startISO, endISO }: ActivityCountFilter): Promise<number> => {
    let q = client().from('activities').select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId).eq('assigned_to', assignedTo);
    if (startISO) q = q.gte('created_at', startISO);
    if (endISO) q = q.lt('created_at', endISO);
    const { count } = await q;
    return count || 0;
  };

  countLostCards = async ({ workspaceId, startISO, endISO }: LostCardCountFilter): Promise<number> => {
    let q = client().from('cards').select('id', { count: 'exact', head: true })
      .filter('workspace_id', 'eq', workspaceId).eq('status', 'lost');
    if (startISO) q = q.gte('lost_at', startISO);
    if (endISO) q = q.lt('lost_at', endISO);
    const { count } = await q;
    return count || 0;
  };

  listNegotiationLeads = async ({ workspaceId, userId, restrictToUser, startISO, endISO }: NegotiationLeadsFilter): Promise<NegotiationLeadRow[]> => {
    let q = client().from('leads').select('id, proposta, status, sdr_id, closer_id')
      .eq('workspace_id', workspaceId).eq('ativo', true)
      .not('proposta', 'is', null).not('status', 'in', '(won,lost)');
    if (startISO) q = q.gte('created_at', startISO);
    if (endISO) q = q.lt('created_at', endISO);
    if (restrictToUser) q = q.or(`sdr_id.eq.${userId},closer_id.eq.${userId}`);
    const { data } = await q;
    return (data || []) as unknown as NegotiationLeadRow[];
  };

  listSalesLeadIds = async (workspaceId: string, leadIds: string[]): Promise<string[]> => {
    if (!leadIds.length) return [];
    const { data } = await client().from('sales').select('lead_id').eq('workspace_id', workspaceId).in('lead_id', leadIds);
    return (data || []).map((s) => s.lead_id).filter((id): id is string => Boolean(id));
  };

  countMeetings = async ({ workspaceId, sdrId, closerId, status, excludeCancelled, startISO, endISO }: MeetingCountFilter): Promise<number> => {
    let q = client().from('meetings').select('id', { count: 'exact', head: true }).eq('workspace_id', workspaceId);
    if (sdrId) q = q.eq('sdr_id', sdrId);
    if (closerId) q = q.eq('closer_id', closerId);
    if (status) q = q.eq('status', status as 'done' | 'cancelled' | 'scheduled' | 'confirmed' | 'no_show');
    if (excludeCancelled) q = q.neq('status', 'cancelled');
    if (startISO) q = q.gte('scheduled_at', startISO);
    if (endISO) q = q.lt('scheduled_at', endISO);
    const { count } = await q;
    return count || 0;
  };

  countActiveCards = async ({ workspaceId, closerId, status }: CardCountFilter): Promise<number> => {
    // `cards` não expõe workspace_id no schema tipado; usa-se .filter (coluna string).
    let q = client().from('cards').select('id', { count: 'exact', head: true }).filter('workspace_id', 'eq', workspaceId);
    if (closerId) q = q.eq('closer_id', closerId);
    if (status) q = q.eq('status', status);
    const { count } = await q;
    return count || 0;
  };

  countCommissions = async ({ workspaceId, closerId, startDate, endDate }: CommissionCountFilter): Promise<number> => {
    let q = client().from('commissions').select('id', { count: 'exact', head: true }).eq('workspace_id', workspaceId);
    if (closerId) q = q.eq('closer_id', closerId);
    if (startDate) q = q.gte('won_date', startDate);
    if (endDate) q = q.lte('won_date', endDate);
    const { count } = await q;
    return count || 0;
  };

  listOpenLeads = async (workspaceId: string, sdrId: string | null, limit: number): Promise<StaleLeadRow[]> => {
    let q = client()
      .from('leads')
      .select('id, nome, updated_at')
      .eq('workspace_id', workspaceId)
      .eq('ativo', true)
      .not('status', 'in', '("won","lost")')
      .order('updated_at', { ascending: true })
      .limit(limit);
    if (sdrId) q = q.eq('sdr_id', sdrId);
    const { data } = await q;
    return (data || []) as unknown as StaleLeadRow[];
  };

  listRecentActivityLeadIds = async (workspaceId: string, sinceISO: string): Promise<string[]> => {
    const { data } = await client()
      .from('activities').select('lead_id').eq('workspace_id', workspaceId).gte('created_at', sinceISO);
    return (data || []).map((a) => a.lead_id).filter((id): id is string => Boolean(id));
  };

  listSalesByCardIds = async (workspaceId: string, cardIds: string[]): Promise<SaleByCardRow[]> => {
    if (!cardIds.length) return [];
    const { data } = await client()
      .from('sales')
      .select('id, card_id, valor_total, valor_entrada, valor_restante, data_pagamento_restante, forma_pagamento, status_pagamento, data_venda')
      .eq('workspace_id', workspaceId)
      .in('card_id', cardIds);
    return (data || []) as unknown as SaleByCardRow[];
  };
}
